import { on } from '@ember/modifier';
import { action, set } from '@ember/object';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupListItem, Service } from 'webapp-types';
import { GenieBuildBoardEntryPoint } from 'webapp/enums/ai-chat';
import { BillingFeature, BoardPopupShareTab } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import CommonAutoCreateOrCompleteBoardButtonComponent from 'webapp/components/common/auto-create-or-complete-board-button';
import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonColorsAndThemesComponent from 'webapp/components/common/colors-and-themes';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import NotificationsButtonComponent from 'webapp/components/notifications/button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import type DesignerController from 'webapp/controllers/designer';

import type {
	TrackingOriginForDataboardDuplicate,
	TrackingOriginForFavoriteBoardAdded,
} from 'webapp/models-custom/board';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import { HiddenTourStep } from 'webapp/models-custom/tour/designer/option-hidden';
import type BoardRecord from 'webapp/models/board';

import type { DesignerTourSettings } from 'webapp/services/flow/tour/designer';
import type { NewBlockData } from 'webapp/services/model-manager/blocks';
import type {
	TrackingOriginForTemplateCreated,
	TrackingOriginType,
	TrackingOriginTypeForAddReport,
} from 'webapp/services/model-manager/boards';

const MAX_VISIBLE_ITEMS = 3;

type Action =
	| 'openWallVisualSettings'
	| 'openWallShareSettings'
	| 'addBoardToReport'
	| 'addBoardToLoop'
	| 'copyUrl'
	| 'openFullscreenView'
	| 'downloadJpg'
	| 'openAccountTemplateEditPopup'
	| 'openPublicTemplateEditPopup'
	| 'toggleFavorite'
	| 'verify'
	| 'copyBoard'
	| 'deleteBoard';

type Option = PopupListItem<Action>;

interface BoardOptionsTourSettings extends DesignerTourSettings {
	optionAction: Action;
}

interface Args {
	model: Board;
	width: number;
	designerController: DesignerController;
	onBoardUpdate: () => void;
	onBoardDelete: () => void;
	onBoardCopy: () => void;
	onLoopChange: (boardLoop: BoardLoop) => void;
	isWallView?: boolean; // wall view
	currentZoom?: number; // mobile view
}

export default class BoardSettingsComponent extends Component<Args> {
	@tracked isTagsPopupOpen = false;
	@tracked isPermissionsPopupOpen = false;
	@tracked isDownloadingSnapshot = false;

	availableTourSettings: BoardOptionsTourSettings[] = [
		{
			id: TourId.DESIGNER__SHARABLE_LINK,
			optionAction: 'copyUrl',
			checkStartConditions: this.checkSharableLinkTourEligibility.bind(this),
		},
		{
			id: TourId.DESIGNER__ADD_TO_REPORT,
			optionAction: 'addBoardToReport',
			checkStartConditions: this.checkAddToReportTourEligibility.bind(this),
		},
	];
	trackingOrigin: TrackingOriginType = 'designer';
	completeEntryPoint = GenieBuildBoardEntryPoint.COMPLETE_WITH_AI;

	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get options(): Option[] {
		const model = this.args.model;

		if (!model) {
			return [];
		}

		const { isNew, canCurrentUserEdit, isAccountTemplate, isPublicTemplate } = model;
		const options: Option[] = [];

		if (!this.showVisualSettingsAsButton) {
			options.push({
				id: 'visual_settings',
				icon: 'Brush',
				text: 'Visuals',
				action: 'openWallVisualSettings',
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: 'Share',
				text: 'Share',
				action: 'openWallShareSettings',
				isDisabled: isNew,
			});
		}

		options.push({
			icon: 'FullScreen',
			text: 'Full-screen view',
			action: 'openFullscreenView',
			isDisabled: isNew,
		});

		if (!isAccountTemplate) {
			options.push({
				icon: 'Link',
				text: 'Copy URL',
				action: 'copyUrl',
				class: 'link',
				isDisabled: isNew,
			});

			options.push({
				icon: 'Reports',
				text: 'Add to report',
				action: 'addBoardToReport',
				class: 'add-to-report',
				isDisabled: isNew,
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: 'Loop',
				text: 'Add to loop',
				action: 'addBoardToLoop',
				isDisabled: isNew,
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: 'Download',
				text: 'Download',
				action: 'downloadJpg',
				isDisabled: isNew,
				isLoading: this.isDownloadingSnapshot,
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: 'FolderUpload',
				text: 'Save as template',
				action: 'openAccountTemplateEditPopup',
				isDisabled: isNew,
				extra: !this.billingService.accountTemplatesEnabled
					? { type: 'featureLimitation' }
					: undefined,
			});
		}

		if (this.accountService.isPublictemplatesSpace && !isAccountTemplate) {
			options.push({
				icon: 'FolderUpload',
				text: (isPublicTemplate ? 'Edit' : 'Save as') + ' public template',
				action: 'openPublicTemplateEditPopup',
				isDisabled: isNew,
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: 'Copy',
				text: 'Duplicate',
				action: 'copyBoard',
				isDisabled: isNew,
			});
		}

		if (!isAccountTemplate) {
			options.push({
				icon: model.favorite ? 'StarFill' : 'Star',
				text: model.favorite ? 'Unfavorite' : 'Favorite',
				action: 'toggleFavorite',
				class: model.favorite ? 'accent' : '',
				isDisabled: isNew,
			});
		}

		if (!isAccountTemplate && canCurrentUserEdit) {
			options.push({
				icon: 'Verified',
				text: model.isVerified ? 'Unverify' : 'Verify',
				action: 'verify',
				isDisabled: isNew || !model.verificationInfo,
			});
		}

		options.push({
			icon: 'Trash',
			text: 'Delete',
			action: 'deleteBoard',
			class: 'delete',
			isDisabled: isNew || !canCurrentUserEdit,
		});

		return options;
	}

	get showVisualSettingsAsButton() {
		return !!this.args.isWallView;
	}

	get dataPerformanceSummaryObject() {
		/* Hide until settings are re-designed
		if (!this.args.model.isTemplate) {
			return {
				objectType: DataPerformanceSummaryType.BOARD,
				objectId: this.args.model.id || '',
			};
		} */

		return null;
	}

	get showTemplateLimtation() {
		return !this.mmBoardsService.checkIfCanUseTemplate(
			!!this.args.model.templateRecord?.isPublic,
		);
	}

	get isCompleteWithAIBtnDisabled() {
		return this.args.model.checkIsWallFilled();
	}

	saveBoard(): void {
		this.args.onBoardUpdate();
	}

	@action
	openWallVisualSettings(): void {
		this.analyticsService.event(TrackingEvent.DATABOARD_VISUAL_SETTINGS_OPEN);
		this.mmBoardsService.openVisualSettingsPopup(this.args.model);
	}

	openWallShareSettings(): void {
		this.args.model.openWallShareSettings(this.trackingOrigin, '', BoardPopupShareTab.SEND);
	}

	addBoardToReport(): void {
		this.flowTourDesignerService.endTour(TourId.DESIGNER__ADD_TO_REPORT);
		this.analyticsService.event(TrackingEvent.DESIGNER_REPORT_ADD);
		this.mmBoardsService.addToReport(
			this.args.model,
			this.trackingOrigin as TrackingOriginTypeForAddReport,
		);
	}

	addBoardToLoop(): void {
		this.analyticsService.event(TrackingEvent.DESIGNER_LOOP_ADD);
		this.mmBoardsService
			.addToLoop(this.args.model, 'designer_bar')
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	copyUrl(): void {
		this.flowTourDesignerService.endTour(TourId.DESIGNER__SHARABLE_LINK);
		this.args.model.openWallShareSettings(this.trackingOrigin);
	}

	openFullscreenView(): void {
		window.open(this.args.model.internalUrl);
	}

	async downloadJpg() {
		if (this.isDownloadingSnapshot) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		this.isDownloadingSnapshot = true;
		await this.mmBoardsService.downloadSnapshotWithInfolayer(this.args.model);
		this.isDownloadingSnapshot = false;
	}

	openAccountTemplateEditPopup(): void {
		this.mmBoardsService.openAccountTemplateEditPopup(
			this.args.model,
			this.trackingOrigin as TrackingOriginForTemplateCreated,
		);
	}

	openPublicTemplateEditPopup(): void {
		this.mmBoardsService.openPublicTemplateEditPopup(this.args.model);
	}

	toggleFavorite(): void {
		this.args.model.toggleFavorite(this.trackingOrigin as TrackingOriginForFavoriteBoardAdded);
	}

	@action
	verify(): void {
		void this.mmBoardsService.toggleVerification(this.args.model);
	}

	copyBoard(): void {
		const showPopup = this.args.model.sourcesRecords.filter((item) => item.type).length > 0;
		this.args.model.copyBoard(
			this.trackingOrigin as TrackingOriginForDataboardDuplicate,
			showPopup,
			this.args.onBoardCopy,
		);
	}

	deleteBoard(): void {
		this.args.onBoardDelete();
	}

	checkGeneralEligibilityForTour(): boolean {
		return isAlive(this);
	}

	checkSharableLinkTourEligibility(): boolean {
		return this.checkGeneralEligibilityForTour() && this.args.model.allBlocks.length >= 2;
	}

	checkAddToReportTourEligibility(): boolean {
		return (
			this.checkSharableLinkTourEligibility() &&
			this.mmBoardsService.normalBoardModels.length >= 2
		);
	}

	@action
	doAction(actionName?: string): void {
		if (!actionName) {
			return;
		}

		void this[actionName as Action]();
	}

	@action
	colorChanged(color: string): void {
		set(this.args.model, 'color', color);
		once(this, this.saveBoard);
	}

	@action
	themeChanged(theme: number): void {
		set(this.args.model, 'themeId', theme);
		once(this, this.saveBoard);
	}

	@action
	useTemplate(): void {
		const template = this.mmTemplatesService.createModel(this.args.model.templateRecord!),
			afterUse = (board: BoardRecord) => {
				this.navigationService.transitionToRoute('designer.board', board);
			};

		this.mmBoardsService.createBoardFromTemplate(template, 'account_template', afterUse);
	}

	@action
	addMetric() {
		if (this.mmBoardsService.handleFullBoard(this.args.model)) {
			return;
		}

		void this.mmMetricsService
			.openMetricSelectPopup()
			.then((metricSetupData: DSSetup.MetricSetupData) => {
				if (metricSetupData.spaceAccessId) {
					const data: NewBlockData = {
						fromMetricSetup: metricSetupData,
						fromMetricDateRange: this.args.model.commonBlockDateRange?.formula,
					};
					this.args.designerController.setNewBlockPositionAndSize(data);

					this.mmBlocksService
						.addBlockToBoardInDesigner(
							this.args.model,
							data,
							undefined,
							this.args.designerController.activeDatawallGrid || undefined,
						)
						.then((blockRecord) => {
							const boardId = this.args.model.id,
								blockId = blockRecord.id;

							this.navigationService.replaceRoute(
								`/layout-designer/${boardId}/${blockId}`,
							);
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
			});
	}

	@action
	onCollapsibleMoreOpen() {
		this.flowTourDesignerService.endTour(TourId.DESIGNER__MORE_OPTION);
	}

	@action
	setupView(_node: HTMLElement): void {
		const tourSettings = this.availableTourSettings;

		tourSettings.forEach((tourSetting) => {
			this.flowTourDesignerService.bindTourCheck(tourSetting.id, this, () => {
				if (tourSetting.checkStartConditions()) {
					const optionIndex = this.options.findIndex(
						(o) => o.action === tourSetting.optionAction,
					);
					const isSettingVisibleInBar =
						optionIndex >= 0 && optionIndex < MAX_VISIBLE_ITEMS;
					if (!isSettingVisibleInBar) {
						this.flowTourDesignerService.startHiddenTour(
							HiddenTourStep.BOARD_OPTIONS_HIDDEN,
						);
					} else {
						this.flowTourDesignerService.startTour(tourSetting.id);
					}
				}
			});
		});

		const tourId = TourId.DESIGNER__MORE_OPTION;
		this.flowTourDesignerService.bindTourCheck(tourId, this, () => {
			if (this.checkGeneralEligibilityForTour()) {
				this.flowTourDesignerService.startTour(tourId);
			}
		});

		this.flowTourDesignerService.checkTour();
	}

	@action
	teardownView(): void {
		this.availableTourSettings.forEach((tourSetting) => {
			this.flowTourDesignerService.unbindTourCheck(tourSetting.id, this);
		});

		this.flowTourDesignerService.unbindTourCheck(TourId.DESIGNER__MORE_OPTION, this);
	}
	<template>
		<div
			id="boardSettings"
			class={{if @isWallView "desktop" "mobile"}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>

			<div class="dbx-ui-actions">

				{{#if @model.isAccountTemplate}}
					<div
						class="template-label template-label--{{if @isWallView 'normal' 'short'}}"
					></div>
				{{/if}}

				<CommonColorsAndThemesComponent
					@selectedColor={{@model.color}}
					@selectedTheme={{@model.themeId}}
					@onColorChange={{this.colorChanged}}
					@onThemeChange={{this.themeChanged}}
					@reportSlide={{null}}
					@theme="dark"
					@allowCustom={{true}}
				/>

				{{#if this.showVisualSettingsAsButton}}
					<SubmitButtonComponent
						@text="Visuals"
						@theme="empty"
						@onTrigger={{this.openWallVisualSettings}}
						@icon="Brush"
					/>
				{{/if}}

				{{#unless @model.isAccountTemplate}}
					<NotificationsButtonComponent
						@model={{@model}}
						@origin="designer"
						@popupTheme="dark"
						@buttonTheme="empty"
					/>
				{{/unless}}

				<CommonCollapsiblePillOptionsComponent
					@itemsList={{this.options}}
					@doAction={{this.doAction}}
					@maxVisibleItems={{2}}
					@onMoreOpen={{this.onCollapsibleMoreOpen}}
				/>

			</div>

			<div class="actions-right">

				{{#if this.dataPerformanceSummaryObject}}
					<DataPerformanceSummaryButtonComponent
						@object={{this.dataPerformanceSummaryObject}}
						@trackingOrigin={{this.trackingOrigin}}
					/>
				{{/if}}

				{{#if @model.isAccountTemplate}}
					<div
						id="boardTemplateBtn"
						class="button"
						role="button"
						{{on "click" this.useTemplate}}
					>
						Use this template
						{{#if this.showTemplateLimtation}}
							<BillingLimitationWarningComponent />
						{{/if}}
					</div>
				{{else}}
					{{#if @isWallView}}
						<CommonAutoCreateOrCompleteBoardButtonComponent
							@entryPoint={{this.completeEntryPoint}}
							@model={{@model}}
							@activeGridWall={{@designerController.activeDatawallGrid}}
							@text="Complete with AI"
							@isDisabled={{this.isCompleteWithAIBtnDisabled}}
						/>
					{{/if}}

					<SubmitButtonComponent
						id="boardTemplateBtn"
						@onTrigger={{this.addMetric}}
						@icon="Plus"
						@text={{if @isWallView "Add metric" ""}}
						@ripplerEffect={{false}}
					/>
				{{/if}}
			</div>
		</div>
	</template>
}
